import React, { useEffect, useState } from "react";

const DirectTaxSettings = ({ initial = {}, onSave }) => {
  const [form, setForm] = useState({
    pan: initial.pan || "",
    tan: initial.tan || "",
  });

  // =====================================
  // LOAD INITIAL DATA
  // =====================================

  useEffect(() => {
    setForm({
      pan: initial.pan || "",
      tan: initial.tan || "",
    });
  }, [initial]);

  // =====================================
  // HANDLE INPUT CHANGE
  // =====================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value.toUpperCase(),
    }));
  };

  // =====================================
  // PAN VALIDATION
  // Format: ABCDE1234F
  // =====================================

  const validatePAN = (pan) => {
    if (!pan) return true;

    return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan);
  };

  // =====================================
  // TAN VALIDATION
  // Format: ABCD12345E
  // =====================================

  const validateTAN = (tan) => {
    if (!tan) return true;

    return /^[A-Z]{4}[0-9]{5}[A-Z]$/.test(tan);
  };

  // =====================================
  // SAVE
  // =====================================

  const handleSubmit = (e) => {
    e.preventDefault();

    const pan = form.pan.trim().toUpperCase();
    const tan = form.tan.trim().toUpperCase();

    // Validate PAN
    if (!validatePAN(pan)) {
      alert(
        "Please enter a valid PAN number.\n\nExample: ABCDE1234F"
      );
      return;
    }

    // Validate TAN
    if (!validateTAN(tan)) {
      alert(
        "Please enter a valid TAN number.\n\nExample: ABCD12345E"
      );
      return;
    }

    // Send only PAN and TAN to Header
    if (onSave) {
      onSave({
        pan: pan || null,
        tan: tan || null,
      });
    }
  };

  // =====================================
  // STYLES
  // =====================================

  const inputClass =
    "w-full h-9 px-3 text-sm border border-gray-300 rounded-md " +
    "bg-white text-gray-800 " +
    "focus:outline-none focus:ring-1 focus:ring-blue-500 " +
    "focus:border-blue-500 " +
    "placeholder:text-gray-400";

  const labelClass =
    "block text-xs font-medium text-gray-600 mb-1";

  // =====================================
  // UI
  // =====================================

  return (
    <div className="w-full max-w-5xl mx-auto px-4 py-3">

      <form onSubmit={handleSubmit}>

        {/* =====================================
            HEADER
        ===================================== */}

        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-800">
            Direct Tax
          </h2>

          <p className="text-xs text-gray-500 mt-0.5">
            Manage your organisation's direct tax identification details.
          </p>
        </div>

        {/* =====================================
            DIRECT TAX INFORMATION
        ===================================== */}

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">

          {/* Section Header */}
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">
              Direct Tax Information
            </h3>
          </div>

          {/* Section Body */}
          <div className="p-4">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">

              {/* =====================================
                  PAN
              ===================================== */}

              <div>
                <label
                  htmlFor="pan"
                  className={labelClass}
                >
                  PAN Number
                </label>

                <input
                  id="pan"
                  name="pan"
                  type="text"
                  value={form.pan}
                  onChange={handleChange}
                  maxLength={10}
                  autoComplete="off"
                  placeholder="ABCDE1234F"
                  className={inputClass}
                />

                <p className="text-[11px] text-gray-400 mt-1">
                  Permanent Account Number
                </p>
              </div>

              {/* =====================================
                  TAN
              ===================================== */}

              <div>
                <label
                  htmlFor="tan"
                  className={labelClass}
                >
                  TAN Number
                </label>

                <input
                  id="tan"
                  name="tan"
                  type="text"
                  value={form.tan}
                  onChange={handleChange}
                  maxLength={10}
                  autoComplete="off"
                  placeholder="ABCD12345E"
                  className={inputClass}
                />

                <p className="text-[11px] text-gray-400 mt-1">
                  Tax Deduction and Collection Account Number
                </p>
              </div>

            </div>
          </div>
        </div>

        {/* =====================================
            SAVE BUTTON
        ===================================== */}

        <div className="flex justify-end mt-4">

          <button
            type="submit"
            className="
              h-9
              px-5
              bg-blue-600
              text-white
              rounded-md
              hover:bg-blue-700
              active:bg-blue-800
              text-sm
              font-medium
              transition
              focus:outline-none
              focus:ring-2
              focus:ring-blue-500
              focus:ring-offset-1
            "
          >
            Save Changes
          </button>

        </div>

      </form>
    </div>
  );
};

export default DirectTaxSettings;